












































































































































































import {
  defineComponent,
  ref,
  computed,
  watch,
  useRoute,
  useFetch,
  useRouter
} from '@nuxtjs/composition-api'
import { useDep } from '~/compositions/dependency-container'
import { useLogger } from '~/compositions/logger'
import { useUserAgent } from '~/compositions/user-agent'
import PriceTrendsService from '~/services/PriceTrendsService'
import {
  Filters,
  SelectedFilter,
  StatLocation
} from '~/models/statistics/types'
import LocationItem from '~/components/plot/price-trends/LocationItem.vue'
import SelectedFilters from '~/components/plot/price-trends/SelectedFilters.vue'
import FiltersSection from '~/components/plot/price-trends/FiltersSection.vue'
import CContainer from '~/components/shared/configurable/grid/CContainer.vue'
import CPicture from '~/components/shared/configurable/image/CPicture.vue'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import CIcon from '~/components/shared/configurable/CIcon.vue'
import CLink from '~/components/shared/configurable/link/CLink.vue'
import CButton from '~/components/shared/configurable/button/CButton.vue'
import { ciFilterCategoriesV2 } from '~/icons/source/regular/filter-categories-v2'
import { ciTimes } from '~/icons/source/regular/times'
import CMobileSidebar from '~/components/shared/configurable/CMobileSidebar.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import CSpinner from '~/components/shared/configurable/CSpinner.vue'
import { camelToUnderscore } from '~/utils/snake-case'
import { vue3Set } from '~/utils/nuxt3-migration'

export default defineComponent({
  head() {
    return {
      title: this.$t('property prices in greece'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$t('property_prices_tables_description')
        },
        {
          hid: 'og:title',
          property: 'og:title',
          content: this.$t('property prices in greece')
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this.$t('property_prices_tables_description')
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.$s(
            '/nuxt-static/img/landing-banners/plot_pricetrends_desktop.svg'
          )
        }
      ],
      link: [
        {
          rel: 'preload',
          href: this.isMobile
            ? this.$s(
                '/nuxt-static/img/landing-banners/plot_pricetrends_mobile.svg'
              )
            : this.$s(
                '/nuxt-static/img/landing-banners/plot_pricetrends_desktop.svg'
              ),
          as: 'image'
        }
      ]
    }
  },
  components: {
    CContainer,
    CPicture,
    CImg,
    CIcon,
    CLink,
    CButton,
    CSpinner,
    LocationItem,
    FiltersSection,
    SelectedFilters,
    CMobileSidebar
  },
  setup() {
    const priceTrendsService = useDep(PriceTrendsService)
    const route = useRoute()
    const router = useRouter()
    const logger = useLogger()
    const { isPc, isMobile } = useUserAgent()

    const loading = ref(false)
    const showMobileFacets = ref(false)
    const statsList = ref<StatLocation[]>([])
    const fetchedFilters = ref<Filters>({} as Filters)

    const hasSelectedFilters = computed(() => {
      return Object.values(fetchedFilters.value).some(
        filter => filter.selected.length > 0
      )
    })

    const openMobileFacets = () => {
      showMobileFacets.value = true
      document.body.classList.add('modal-open')
    }

    const closeMobileFacets = () => {
      showMobileFacets.value = false
      document.body.classList.remove('modal-open')
    }

    const clearFilters = () => {
      closeMobileFacets()
      router.push({ name: '__price-trends' })
    }

    const changeFilters = (data: SelectedFilter) => {
      const query = { ...route.value.query }
      const paramName = camelToUnderscore(data.type)
      const paramValue = data.value.toString()

      if (query[paramName]) {
        if (Array.isArray(query[paramName])) {
          if (query[paramName].includes(paramValue)) {
            const filteredValues = (query[paramName] as (
              | string
              | null
            )[]).filter(value => value !== paramValue)
            if (filteredValues.length === 1) {
              vue3Set(query, paramName, filteredValues[0])
            } else if (filteredValues.length === 0) {
              delete query[paramName]
            } else {
              vue3Set(query, paramName, filteredValues)
            }
          } else {
            vue3Set(query, paramName, query[paramName].concat(paramValue))
          }
        } else if (query[paramName] === paramValue) {
          delete query[paramName]
        } else {
          vue3Set(query, paramName, [query[paramName] as string, paramValue])
        }
      } else {
        vue3Set(query, paramName, paramValue)
      }

      router.push({ query })
    }

    const fetchData = async () => {
      loading.value = true
      try {
        const { stats, filters } = await priceTrendsService.getPriceTrends(
          route.value.query
        )
        statsList.value = stats
        fetchedFilters.value = filters
      } catch (error) {
        logger.captureError(error as any)
      } finally {
        loading.value = false
      }
    }

    watch(() => route.value.query, fetchData)

    useFetch(fetchData)

    return {
      statsList,
      fetchedFilters,
      ciFilterCategoriesV2,
      ciTimes,
      isPc,
      isMobile,
      hasSelectedFilters,
      showMobileFacets,
      loading,
      openMobileFacets,
      closeMobileFacets,
      clearFilters,
      changeFilters
    }
  },
  i18n: defineComponentTranslations({
    property_prices_tables_description: {
      en:
        'The tables reflect the number of properties per year, for sale and rent per area, square footage as well as the average price per sq.m.',
      el:
        'Οι πίνακες αποτυπώνουν τον αριθμό των ακινήτων ανα έτος, προς πώληση και ενοικίαση ανά περιοχή, τετραγωνικά καθώς και τη μέση τιμή ανά τ.μ.'
    },
    property_prices: {
      en: 'Property prices',
      el: 'Τιμές ακινήτων'
    },
    no_results: {
      en: 'No results were found for these criteria',
      el: 'Δεν βρέθηκαν αποτελέσματα για αυτά τα κριτήρια'
    },
    no_results_description: {
      en: 'Try changing the filters you have selected to see more results.',
      el:
        'Δοκίμασε να αλλάξεις τα φίλτρα που έχεις επιλέξει για να δεις περισσότερα αποτελέσματα.'
    }
  })
})
